import React, { useCallback, useState } from 'react';
import { FaRegThumbsDown, FaRegThumbsUp, FaThumbsDown, FaThumbsUp } from "react-icons/fa";
import { FaFileInvoice } from 'react-icons/fa6';
import { IoMdStarOutline } from 'react-icons/io';
import { IoChatbubble, IoChatbubbleOutline, IoCheckmarkCircle, IoCheckmarkCircleOutline, IoCloseCircle, IoCloseCircleOutline, IoFileTrayFull, IoFileTrayFullOutline, IoMail, IoMailOutline, IoStar } from 'react-icons/io5';
import { MdOutlineModeEditOutline, MdOutlinePaid, MdOutlineScoreboard, MdPaid, MdScoreboard } from 'react-icons/md';
import { TbFileInvoice } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import { ActionIcon, Switch, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../../components/buttons/DeletePopover';
import OrderControl from '../../../../components/buttons/OrderControl';
import StatusToggle from '../../../../components/buttons/StatusToggle';
import CustomTable from '../../../../components/table/CustomTable';
import useFetch from '../../../../hooks/useFetch';
import useHandleErrors from '../../../../hooks/useHandleErrors';
import { menuRoutes } from '../../../../router/menu';
import { StateService } from '../../../../services/state/stateService';
import { StatesApiResponse } from '../../../../type/entities/state-type';
import { usePrivilege } from '../../../../components/priviledge/PriviledgeProvider';

interface FilteredStatesTableProps {
    entity: string;
    filters: any;
    updateFilters: any;
    updateFilterOrder: any;
    updatePage: any;
    updatePageSize: any;
    openFilters: boolean;
    setOpenFilters: any;
}

const columnOrder = [
    'id',
    'statusOrder',
    'name',
    'color',
    'draftStatus',
    'initialStatus',
    'satisfactoryStatus',
    'unsatisfactoryStatus',
    'isValidForNetworkSettlement',
    'isNegativeForNetworkSettlement',
    'requiresKoReason',
    'clientInvoiceStatus',
    'tutorInvoiceStatus',
    'internalInvoiceStatus',
    'scoreDelivery',
    'scoreRemove',
    'paidStatus',
    'unpaidStatus',
    'sendOrderCoursesInformationEmail',
    'productTypes',
    'description',
    'active',
    'createdAt',
    'updatedAt',
];

const entitiesWithoutDraftStatus = ['contract_product', 'contract_invoicing', 'contract_incidence', 'contract_commission', 'contract_blocked'];

const FilteredStatesTable: React.FC<FilteredStatesTableProps> = ({ entity, filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, openFilters, setOpenFilters }) => {

    // HOOKS

    const { handleErrors } = useHandleErrors();
    const service = new StateService();
    const { userCan } = usePrivilege();

    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const showDraftStatusColumn = (entity.startsWith('contract') || entity === 'contract') && !entitiesWithoutDraftStatus.includes(entity);

    // FUNCTIONS

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (filters.filter_filters?.entity === undefined) return;
        const response = await service.listStates(filters);
        return response.getResponseData() as StatesApiResponse;
    }, [filters]));

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ACTUALIZAR EL ORDEN DEL ESTADO
     * @EN UPDATE THE ORDER OF THE STATUS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const updateStateOrder = async (id: string, order: 'up' | 'down') => {
        const response = await (await service.updateStateOrder(id, order)).getResponseData();

        if (response.success) {
            toast.success('Orden actualizado');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR EL ESTADO (ACTIVO/DESACTIVADO) DEL ESTADO
     * @EN CHANGE THE STATE (ACTIVE/DEACTIVATED) OF THE STATUS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const toggleState = async (id: string, state: boolean) => {
        if (!userCan('edit_statuses', 'statuses')) {
            toast.error('No tienes permisos para editar los estados');
            return;
        }
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleStateStatus(id, state)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(state ? "Se ha desactivado el estado" : "Se ha activado el estado");
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES GENERAR MENSAJE
     * @EN GENERATE MESSAGE
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const generateMessage = (status: string, success: boolean): string => {
        const baseMessage = `Estado ${success ? 'establecido' : 'no se ha podido establecer'} como`;
        const statusDescriptions: { [key: string]: string } = {
            draft: 'borrador',
            initial: 'inicial',
            satisfactory: 'satisfactorio',
            unsatisfactory: 'insatisfactorio',
            requiresKoReason: 'requiere motivo de KO',
            isValidForNetworkSettlement: 'válido para liquidación de red',
            isNegativeForNetworkSettlement: 'ajuste negativo para liquidación de red',
            clientInvoiceStatus: 'generador de factura de cliente',
            tutorInvoiceStatus: 'generador de factura de tutor',
            internalInvoiceStatus: 'generador de factura interna',
            scoreDelivery: 'generador de puntuación del contrato',
            scoreRemove: 'restar puntuación del contrato',
            paidStatus: 'pagado',
            unpaidStatus: 'impagado',
            sendOrderCoursesInformationEmail: 'enviador de email informativo de cursos',
        };
        return `${baseMessage} ${statusDescriptions[status]}`;
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR LOS ESTADOS DE LOS ESTADOS
     * @EN CHANGE THE STATES OF THE STATUS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const toggleStatus = async (id: string, stateType: 'draft' | 'initial' | 'satisfactory' | 'unsatisfactory' | 'requiresKoReason' | 'isValidForNetworkSettlement' | 'isNegativeForNetworkSettlement' | 'clientInvoiceStatus' | 'tutorInvoiceStatus' | 'internalInvoiceStatus' | 'scoreDelivery' | 'scoreRemove' | 'paidStatus' | 'unpaidStatus' | 'sendOrderCoursesInformationEmail') => {
        if (!userCan('edit_statuses', 'statuses')) {
            toast.error('No tienes permisos para editar los estados');
            return;
        }
        if (stateType === 'draft' && !showDraftStatusColumn) {
            toast.error('La entidad seleccionada no permite estados borrador');
            return;
        }
        if (changingStatus.includes(id)) return;
        setChangingStatus((prev) => [...prev, id]);

        const stateServiceMap: { [key: string]: () => Promise<any> } = {
            draft: () => service.toggleDraftState(id),
            initial: () => service.toggleInitialState(id),
            satisfactory: () => service.toggleSatisfactoryState(id),
            unsatisfactory: () => service.toggleUnsatisfactoryState(id),
            requiresKoReason: () => service.toggleRequiresKoReasonState(id),
            isValidForNetworkSettlement: () => service.toggleValidForNetworkSettlementState(id),
            isNegativeForNetworkSettlement: () => service.toggleNegativeForNetworkSettlementState(id),
            clientInvoiceStatus: () => service.toggleClientInvoiceState(id),
            tutorInvoiceStatus: () => service.toggleTutorInvoiceState(id),
            internalInvoiceStatus: () => service.toggleInternalInvoiceState(id),
            scoreDelivery: () => service.toggleScoreDeliveryState(id),
            scoreRemove: () => service.toggleScoreRemoveState(id),
            paidStatus: () => service.togglePaidStatus(id),
            unpaidStatus: () => service.toggleUnpaidStatus(id),
            sendOrderCoursesInformationEmail: () => service.toggleSendOrderCoursesInformationEmailState(id),
        };

        try {
            const response = await (await stateServiceMap[stateType]()).getResponseData();

            if (response.success) {
                toast.success(generateMessage(stateType, true));
                refetch();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            toast.error(generateMessage(stateType, false));
        } finally {
            setChangingStatus((prev) => prev.filter((item) => item !== id));
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR ESTADO
     * @EN DELETE STATUS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        const response = await (await service.deleteState(id)).getResponseData();
        if (response.success) {
            toast.success('Estado eliminado correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR MÚLTIPLES ESTADOS
     * @EN DELETE MULTIPLE STATUS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        const response = await (await service.deleteMultiStates(ids)).getResponseData();
        if (response.success) {
            toast.success('Estados eliminados correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ORDENAR LOS DATOS DE ACUERDO A LA ORDEN DE LAS COLUMNAS
     * @EN ORDER THE DATA ACCORDING TO THE ORDER OF THE COLUMNS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <>
            {data && data !== undefined && (
                <div key={data.totalRegisters}>
                    <CustomTable
                        largeTable
                        id={'states-table'}
                        columnOrder={columnOrder}
                        data={orderedData()}
                        isLoading={loading}
                        columnsNotShown={[
                            'entity', 'updatedAt',
                            entity !== 'contract_product' ? 'clientInvoiceStatus' : '',
                            entity !== 'contract_product' ? 'tutorInvoiceStatus' : '',
                            entity !== 'contract_product' ? 'internalInvoiceStatus' : '',
                            entity !== 'contract_product' ? 'scoreDelivery' : '',
                            entity !== 'contract_product' ? 'scoreRemove' : '',
                            !showDraftStatusColumn ? 'draftStatus' : '',
                            entity !== 'contract' ? 'isValidForNetworkSettlement' : '',
                            entity !== 'contract' ? 'isNegativeForNetworkSettlement' : '',
                            entity === 'order' ? 'productType' : '',
                            entity === 'contract_blocked' ? 'productTypes' : '',
                            entity !== 'expiration' ? 'paidStatus' : '',
                            entity !== 'expiration' ? 'unpaidStatus' : '',
                            entity !== 'order' ? 'sendOrderCoursesInformationEmail' : '',
                        ]}
                        overrideColumns={[
                            {
                                key: 'statusOrder',
                                render: (row: any) => {
                                    return (
                                        <OrderControl
                                            id={row.row.original.id}
                                            index={row.row.index}
                                            totalRegisters={data.totalRegisters}
                                            onOrderChange={updateStateOrder}
                                            hideControls={!userCan('update_status_order', 'statuses')}
                                        />
                                    );
                                },
                            },
                            {
                                key: 'productTypes',
                                render: (row: any) => {
                                    return (row.row.original.productTypes || []).map((p: any) => {
                                        return (
                                            <span key={p.productType.id} className="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded-full mr-1">
                                                {p.productType.name}
                                            </span>
                                        );
                                    });
                                },
                            },
                            {
                                key: 'color',
                                render: (row: any) => {
                                    if (!row.row.original?.color) return null;
                                    return (
                                        <div
                                            key={row.row.original.id + row.row.original?.color}
                                            style={{
                                                backgroundColor: row.row.original?.color,
                                                border: "1px solid rgba(0, 0, 0, 0.1)",
                                                width: "35px",
                                                height: "35px",
                                            }}
                                            className={'rounded-full mr-3 p-3'}
                                        />
                                    );
                                },
                            },
                            {
                                key: 'draftStatus',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.draftStatus}
                                            onToggle={() => toggleStatus(row.row.original.id, 'draft')}
                                            ActiveIcon={IoFileTrayFull}
                                            InactiveIcon={IoFileTrayFullOutline}
                                            tooltipMessage="Establecer como borrador"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'initialStatus',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.initialStatus}
                                            onToggle={() => toggleStatus(row.row.original.id, 'initial')}
                                            ActiveIcon={IoStar}
                                            InactiveIcon={IoMdStarOutline}
                                            tooltipMessage="Establecer como inicial"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'satisfactoryStatus',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.satisfactoryStatus}
                                            onToggle={() => toggleStatus(row.row.original.id, 'satisfactory')}
                                            ActiveIcon={FaThumbsUp}
                                            InactiveIcon={FaRegThumbsUp}
                                            tooltipMessage="Establecer como satisfactorio"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'unsatisfactoryStatus',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.unsatisfactoryStatus}
                                            onToggle={() => toggleStatus(row.row.original.id, 'unsatisfactory')}
                                            ActiveIcon={FaThumbsDown}
                                            InactiveIcon={FaRegThumbsDown}
                                            tooltipMessage="Establecer como insatisfactorio"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'requiresKoReason',
                                columnNameKey: 'motivo',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    if (!row.row.original.unsatisfactoryStatus) {
                                        return (
                                            <Tooltip size="sm" content="Solo aplica a estados insatisfactorios" placement="top" color="invert">
                                                <div className="w-min opacity-40">
                                                    <IoChatbubbleOutline size={23} color="#b7b8b9" />
                                                </div>
                                            </Tooltip>
                                        );
                                    }
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.requiresKoReason}
                                            onToggle={() => toggleStatus(row.row.original.id, 'requiresKoReason')}
                                            ActiveIcon={IoChatbubble}
                                            InactiveIcon={IoChatbubbleOutline}
                                            tooltipMessage="Requiere motivo de KO"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'isValidForNetworkSettlement',
                                columnNameKey: 'válido liquidación red',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.isValidForNetworkSettlement}
                                            onToggle={() => toggleStatus(row.row.original.id, 'isValidForNetworkSettlement')}
                                            ActiveIcon={IoCheckmarkCircle}
                                            InactiveIcon={IoCheckmarkCircleOutline}
                                            tooltipMessage="Establecer como válido para liquidación de red"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'isNegativeForNetworkSettlement',
                                columnNameKey: 'ajuste negativo red',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.isNegativeForNetworkSettlement}
                                            onToggle={() => toggleStatus(row.row.original.id, 'isNegativeForNetworkSettlement')}
                                            ActiveIcon={IoCloseCircle}
                                            InactiveIcon={IoCloseCircleOutline}
                                            tooltipMessage="Establecer como ajuste negativo para liquidación de red"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'clientInvoiceStatus',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.clientInvoiceStatus}
                                            onToggle={() => toggleStatus(row.row.original.id, 'clientInvoiceStatus')}
                                            ActiveIcon={FaFileInvoice}
                                            InactiveIcon={TbFileInvoice}
                                            tooltipMessage="Establecer como generador de factura de cliente"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'tutorInvoiceStatus',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.tutorInvoiceStatus}
                                            onToggle={() => toggleStatus(row.row.original.id, 'tutorInvoiceStatus')}
                                            ActiveIcon={FaFileInvoice}
                                            InactiveIcon={TbFileInvoice}
                                            tooltipMessage="Establecer como generador de factura de tutor"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'internalInvoiceStatus',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.internalInvoiceStatus}
                                            onToggle={() => toggleStatus(row.row.original.id, 'internalInvoiceStatus')}
                                            ActiveIcon={FaFileInvoice}
                                            InactiveIcon={TbFileInvoice}
                                            tooltipMessage="Establecer como generador de factura interna"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'scoreDelivery',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.scoreDelivery}
                                            onToggle={() => toggleStatus(row.row.original.id, 'scoreDelivery')}
                                            ActiveIcon={MdScoreboard}
                                            InactiveIcon={MdOutlineScoreboard}
                                            tooltipMessage="Establecer como generador de puntuación del contrato"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'scoreRemove',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.scoreRemove}
                                            onToggle={() => toggleStatus(row.row.original.id, 'scoreRemove')}
                                            ActiveIcon={MdScoreboard}
                                            InactiveIcon={MdOutlineScoreboard}
                                            tooltipMessage="Establecer como generador de puntuación del contrato"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'paidStatus',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.paidStatus}
                                            onToggle={() => toggleStatus(row.row.original.id, 'paidStatus')}
                                            ActiveIcon={MdPaid}
                                            InactiveIcon={MdOutlinePaid}
                                            tooltipMessage="Establecer como generador de vencimiento pagado"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'unpaidStatus',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.unpaidStatus}
                                            onToggle={() => toggleStatus(row.row.original.id, 'unpaidStatus')}
                                            ActiveIcon={IoCloseCircle}
                                            InactiveIcon={IoCloseCircleOutline}
                                            tooltipMessage="Establecer como generador de vencimiento impagado"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'sendOrderCoursesInformationEmail',
                                permissions: [{ group: 'statuses', permission: 'edit_statuses' }],
                                render: (row: any) => {
                                    return (
                                        <StatusToggle
                                            isActive={row.row.original.sendOrderCoursesInformationEmail}
                                            onToggle={() => toggleStatus(row.row.original.id, 'sendOrderCoursesInformationEmail')}
                                            ActiveIcon={IoMail}
                                            InactiveIcon={IoMailOutline}
                                            tooltipMessage="Enviar email de información de cursos al cambiar a este estado"
                                        />
                                    );
                                }
                            },
                            {
                                key: 'active',
                                render: (row: any) => {
                                    return (
                                        <Tooltip
                                            size="sm"
                                            content={row.row.original.active ? 'Desactivar estado' : 'Activar estado'}
                                            placement="top"
                                            color="invert"
                                        >
                                            <Switch
                                                id={row.row.original.id}
                                                checked={row.row.original.active}
                                                onChange={() => toggleState(row.row.original.id, row.row.original.active)}
                                                switchKnobClassName='bg-secondary'
                                            />
                                        </Tooltip>
                                    );
                                },
                            },
                        ]}
                        actions={[
                            {
                                label: 'Edit',
                                permissions: { group: 'statuses', permission: 'edit_statuses' },
                                render: (row: any) => {
                                    return (
                                        <Tooltip
                                            size="sm"
                                            content={'Editar estado'}
                                            placement="top"
                                            color="invert"
                                        >
                                            <Link to={`${menuRoutes.states.path}/${row.id}/edit`}>
                                                <ActionIcon
                                                    as="span"
                                                    size="sm"
                                                    variant="outline"
                                                    className="hover:!border-gray-900 hover:text-gray-700"
                                                >
                                                    <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                                </ActionIcon>
                                            </Link>
                                        </Tooltip>
                                    );
                                },
                            },
                            {
                                label: 'Delete',
                                permissions: { group: 'statuses', permission: 'delete_statuses' },
                                render: (row: any) => {
                                    return (
                                        <Tooltip
                                            size="sm"
                                            content={'Eliminar estado'}
                                            placement="top"
                                            color="invert"
                                        >
                                            <div>
                                                <DeletePopover
                                                    title={`Eliminar estado`}
                                                    description={`¿Estás seguro de que deseas eliminar el estado ${row.name}?`}
                                                    onDelete={() => handleDelete(row.id)}
                                                />
                                            </div>
                                        </Tooltip>
                                    );
                                },
                            },
                        ]}
                        handleMultipleDelete={handleMultiDelete}
                        filters={filters}
                        updateFilters={updateFilters}
                        updateFilterOrder={updateFilterOrder}
                        defaultOrder={filters.filter_order || undefined}
                        paginationData={{
                            pageSize: filters.limit,
                            currentPage: filters.page,
                            pageCount: (data as StatesApiResponse) ? data.lastPage : 1,
                            totalCount: data?.totalRegisters,
                            handlePagination: updatePage,
                            handlePerPage: updatePageSize,
                        }}
                        toggleFilters={() => setOpenFilters(!openFilters)}
                    />
                </div>
            )}
        </>
    );
};

export default FilteredStatesTable;
